import { ActionTree, GetterTree, MutationTree } from "vuex";
import { ListingSession } from "@/services/session";
import SessionService from '@/services/session';
import ItemService from '@/services/items';
import { Item } from '../services/model/item'
import { maybeSlowLoad } from '@/utils/slowLoad';

class State {
    sessions: Array<Session>;
    activeSessionId?: string;

    constructor(sessions: Array<Session>, activeSessionId?: string) {
        this.sessions = sessions;
        this.activeSessionId = activeSessionId;
    }
}

export class Session {
    id?: string
    createdAt: string
    itemCount: number
    soldCount: number
    items: Array<Item>

    constructor(items: Array<Item>, createdAt: string, itemCount: number, soldCount: number, id?: string) {
        this.id = id;
        this.createdAt = createdAt;
        this.items = items;
        this.itemCount = itemCount;
        this.soldCount = soldCount;
    }
}

const getters = <GetterTree<State, any>>{
    getAllItems: (state: State) => {
        return state.sessions.map(session => session.items)
    },
    getSession: (state: State) => (id: string) => {
        return state.sessions.find(session => session.id === id)
    },
    getActiveSessionId: (state: State) => {
        return state.activeSessionId;
    },
    // TODO: do not return items as well - expensive?
    getSessions: (state: State) => {
        // Create a copy to avoid mutating state directly
        return [...state.sessions].sort((s1: Session, s2: Session) => {
            return Date.parse(s2.createdAt) - Date.parse(s1.createdAt)
        });
    },
    getActiveSessionItemCount: (state: State) => {
        const activeSessionIndex = state.sessions.findIndex(session => session.id == state.activeSessionId)
        if (activeSessionIndex !== -1) {
            return state.sessions[activeSessionIndex].items.length
        }
    },
    getSessionItems: (state: State) => (sessionId: string): Array<Item> => {
        const sessionIndex = state.sessions.findIndex(session => session.id == sessionId)
        if (sessionIndex !== -1) {
            // Return a copy to ensure reactivity - Vue will track changes to the original
            return state.sessions[sessionIndex].items
        } else {
            console.info("No session to get items for.")
            return []
        }
    },
    getActiveSessionItems: (state: State) => {
        const activeSessionIndex = state.sessions.findIndex(session => session.id == state.activeSessionId);
        if (activeSessionIndex !== -1) {
            return state.sessions[activeSessionIndex].items
        } else {
            console.info("No active session to get items for.")
            return []
        }
    }
};

const mutations = <MutationTree<State>>{
    addItem: (state: State, item: Item) => {
        const itemSessionId = item.listingSessionID;
        const sessionIndex: number = state.sessions.findIndex(session => session.id == itemSessionId);
        if (sessionIndex !== -1) {
            state.sessions[sessionIndex].items = [item].concat(state.sessions[sessionIndex].items)
        } else {
            // no session exist, so createdAt is an empty string
            console.log("No session exist for adding this item to.")
            const session = new Session([item], "", 0, 0, item.listingSessionID)
            state.sessions = state.sessions.concat(session)
        }
    },
    setSessionItems: (state: State, sessionItems: { items: Item[], sessionId?: string }) => {
        const itemSessionId = sessionItems.sessionId;
        const sessionIndex: number = state.sessions.findIndex(session => session.id == itemSessionId);
        if (sessionIndex !== -1) {
            state.sessions[sessionIndex].items = sessionItems.items
        } else {
            // no session exist, so createdAt is an empty string
            console.log("No session exist for adding these items to.")
            const session = new Session(sessionItems.items, "", 0, 0, sessionItems.sessionId)
            state.sessions = state.sessions.concat(session)
        }
    },
    loadSessions: (state: State, sessions: Session[]) => {
        const currentSessionIds = state.sessions.map(session => session.id)
        sessions.forEach((session: Session) => {
            const sessionIndex = state.sessions.findIndex(stateSession => stateSession.id == session.id)
            if (sessionIndex !== -1) {
                // if the session already exists in state, only update the metadata of the session
                console.log("session already exist - only updating metadata")
                state.sessions[sessionIndex].createdAt = session.createdAt
                state.sessions[sessionIndex].itemCount = session.itemCount
                state.sessions[sessionIndex].soldCount = session.soldCount
            } else {
                // if the session doesn't exist in state, create a new session (empty items, id, createdAt)
                state.sessions = state.sessions.concat(session)
            }
        })
    },
    setActiveSessionId: (state: State, listingSession: ListingSession) => {
        state.activeSessionId = listingSession.id;
    },
    setActiveSession: (state: State, listingSession: ListingSession) => {
        // Check if session already exists in store
        const sessionExists = state.sessions.some(s => s.id === listingSession.id);
        if (!sessionExists) {
            const session = new Session(
                [], 
                listingSession.createdAt, 
                listingSession.itemCount, 
                listingSession.soldCount, 
                listingSession.id
            );
            state.sessions = state.sessions.concat(session);
        }
    },
    updateSessionItem: (state: State, updatedItem: Item) => {
        // Find the session that contains this item
        const sessionIndex = state.sessions.findIndex(session => session.id === updatedItem.listingSessionID);
        if (sessionIndex !== -1) {
            const itemIndex = state.sessions[sessionIndex].items.findIndex(item => item.id === updatedItem.id);
            if (itemIndex !== -1) {
                // Merge updated properties with existing item to preserve all fields
                const existingItem = state.sessions[sessionIndex].items[itemIndex];
                // Preserve all existing properties and only update what changed
                const mergedItem: Item = {
                    ...existingItem,
                    ...updatedItem,
                    // Explicitly preserve critical fields that might be missing from API response
                    title: updatedItem.title || existingItem.title,
                    author: updatedItem.author || existingItem.author,
                    subtitle: updatedItem.subtitle || existingItem.subtitle,
                    publisher: updatedItem.publisher || existingItem.publisher,
                    description: updatedItem.description || existingItem.description,
                    category: updatedItem.category ?? existingItem.category,
                    price: updatedItem.price ?? existingItem.price,
                    condition: updatedItem.condition || existingItem.condition,
                    binding: updatedItem.binding ?? existingItem.binding,
                    reference: updatedItem.reference || existingItem.reference,
                    imageIDs: updatedItem.imageIDs || existingItem.imageIDs,
                    listingSessionID: updatedItem.listingSessionID || existingItem.listingSessionID,
                    // Explicitly set soldAt from updatedItem (can be undefined when marking unsold)
                    soldAt: updatedItem.soldAt,
                };
                // Create a new array with the updated item to ensure reactivity
                const newItems = [...state.sessions[sessionIndex].items];
                newItems[itemIndex] = mergedItem;
                // Replace the entire session object to ensure Vue reactivity
                const updatedSession = {
                    ...state.sessions[sessionIndex],
                    items: newItems
                };
                // Replace the session in the array
                const newSessions = [...state.sessions];
                newSessions[sessionIndex] = updatedSession;
                state.sessions = newSessions;
            }
        }
    }
};

const actions = <ActionTree<State, any>>{
    createItem: async (context, item: Item) => {
        return ItemService.createItem(item).then(
            response => {
                console.log('Item service response:', response);
                // Add to session store
                context.commit('addItem', item);
                // Also add to items store so Inventory view updates
                context.commit('items/addItem', item, { root: true });
                // Verify state after mutation
                console.log('Store state after commit:', context.state.sessions);
                return response;
            },
            error => {
                console.error('Error creating item:', error);
                throw error;
            }
        )
    },
    updateItem: async (context, item: Item) => {
        const updatedItem = await ItemService.updateItem(item);
        // Update items store (inventory)
        context.commit('items/updateItem', updatedItem, { root: true });
        // Update session store
        context.commit('updateSessionItem', updatedItem);
        return updatedItem;
    },
    populate: async (context, sessionId) => {
        await maybeSlowLoad();

        return SessionService.getItems(sessionId)
            .then((items: Array<Item>) => {
                context.commit('setSessionItems', { items, sessionId })
            })
    },
    getLatestSession: async (context) => {
        return SessionService.getLatestSession()
            .then((listingSession: ListingSession) => {
                context.commit('setActiveSessionId', listingSession);
                context.commit('setActiveSession', listingSession);
                return Promise.resolve(listingSession);
            });
    },
    createSession: async (context) => {
        return SessionService.createSession()
            .then((listingSession: ListingSession) => {
                context.commit('setActiveSessionId', listingSession)
                context.commit('setActiveSession', listingSession)
                return Promise.resolve(listingSession);
            });
    },
    loadSessions: async (context) => {
        return SessionService.getSessions()
            .then((listingSessions: ListingSession[]) => {
                const sessions = listingSessions.map(listingSession =>
                    new Session([], listingSession.createdAt, listingSession.itemCount, listingSession.soldCount, listingSession.id)
                )
                return context.commit('loadSessions', sessions)
            }).then(_ => {
                return context.dispatch("getLatestSession")
            })
    },
    markSold: async (context, item: Item) => {
        // Call the service once and update both stores
        const updatedItem = await ItemService.markItemSold(item);
        // Update items store (commit directly to avoid duplicate API call)
        context.commit('items/updateItem', updatedItem, { root: true });
        // Update session store
        context.commit('updateSessionItem', updatedItem);
        return updatedItem;
    },
    markUnsold: async (context, item: Item) => {
        // Call the service once and update both stores
        const updatedItem = await ItemService.markItemUnsold(item);
        // Update items store (commit directly to avoid duplicate API call)
        context.commit('items/updateItem', updatedItem, { root: true });
        // Update session store
        context.commit('updateSessionItem', updatedItem);
        return updatedItem;
    }
};

const SessionModule = {
    namespaced: true,
    state: new State([], undefined),
    getters: getters,
    mutations: mutations,
    actions: actions
};

export default SessionModule;
